/**
 * The North Reach trial — a fight scaled to the student who walked there.
 *
 * Twenty-five tiles of road end at an island, and this is what is on it: three
 * stages of mathematics drawn from where the student actually is. Two decisions
 * hold it inside the rules the rest of this codebase is written to, and both
 * are the sort that look like details and are not.
 *
 * **The trial is in front of the isle's lock, not behind it.** North Reach is
 * opened by `twenty-mastered`, and what that achievement withholds is the
 * parka and the name — never this. Anybody who walks the road may fight, at any
 * time, having mastered nothing at all. That is exactly the line a kingdom's
 * seal already draws (`types.ts`): a seal withholds the quest line, the boss
 * ceremony and the reward, and never a lesson or a practice page. Mathematics
 * on this platform is never behind a lock, and an island is not an exception —
 * `validateIsles` exists precisely so nobody can quietly make it one.
 *
 * **"Your level" is `workingLevel`, never XP.** Level and gold are habit, and
 * CLAUDE.md is flat about it: nothing unlocks on XP and recommendations never
 * read it. `workingLevel` in `packages/learning/src/recommend.ts` is the
 * platform's *one* answer to "what level is this student at" — stated level,
 * else the placement estimate, else the hardest thing they have done — and it
 * is exported so that a second answer never gets invented. A trial pitched at a
 * student's XP would ask a diligent Grade 4 pupil for calculus, which is the
 * failure mode this whole file is arranged against.
 *
 * The scaling is therefore twofold, and neither half is a clock or a coin: the
 * *concepts* come from the student's own working level, and the *band within
 * the fight* is adaptive, so it climbs when they are right and eases when they
 * are not. A student who has done nothing gets the floor of the curriculum,
 * which is a real trial rather than a locked door.
 *
 * Pure, clock-free and deterministic like every other definition builder here:
 * the same profile at the same level builds the same three stages, so a
 * reloaded fight is the fight that was in progress.
 */

import type { CurriculumRegistry } from '@yuan-math/curriculum';
import type { Difficulty, ExerciseBank } from '@yuan-math/exercises';
import { claimQuestReward, conceptLevelOrder, type LearnerProfile } from '@yuan-math/learning';
import type { EncounterDef } from './encounter.ts';

/** The trial's battle id, and the prefix its reward claim is stored under. */
export const TRIAL_PREFIX = 'trial:';
export const NORTH_TRIAL_ID = 'north-reach';
export const SOUTH_TRIAL_ID = 'south-reach';

/** Every trial on the map, in the order the roads leave the mainland. */
export const TRIAL_IDS = [NORTH_TRIAL_ID, SOUTH_TRIAL_ID] as const;
export type TrialId = (typeof TRIAL_IDS)[number];

export const trialBattleId = (trialId: string): string => `${TRIAL_PREFIX}${trialId}`;
export const trialClaimId = (trialId: string): string => `${TRIAL_PREFIX}${trialId}`;

/** Three stages, four questions each: long enough to mean something, short
 *  enough that a twenty-five tile walk is not followed by a half-hour exam. */
export const TRIAL_STAGES = 3;
export const TRIAL_QUESTIONS = 4;

/**
 * The southern trial is one stage longer, because the road to it is longer and
 * a thirty-tile walk that ends in a shorter fight would read as an anticlimax.
 * Four is also the most this may ever be: past that it stops being a trial and
 * becomes homework at the end of a walk.
 */
export const SOUTH_TRIAL_STAGES = 4;

/** What clearing it pays. Modest on purpose — the walk is most of the reward,
 *  and no cosmetic here is one the wardrobe does not already offer. */
export const TRIAL_XP = 60;
export const TRIAL_GOLD = 45;

/**
 * The concepts a trial asks about, hardest last.
 *
 * Drawn from the levels *at or just below* the student's working level, so the
 * trial is recognisably their own mathematics rather than a spread across the
 * whole curriculum. Only concepts the bank can actually generate for are
 * eligible — an authored-looking stage that throws when it draws a question is
 * the one failure a fight cannot recover from.
 *
 * Sorted by level and then by id, which is what keeps this deterministic: two
 * concepts on the same rung must not swap places between renders, or a reloaded
 * fight would ask different questions.
 */
export function trialConceptIds(
  registry: CurriculumRegistry,
  bank: ExerciseBank,
  level: number,
  count: number = TRIAL_STAGES,
): string[] {
  const teachable = [...registry.graph.concepts.values()]
    .filter((concept) => concept.status === 'available')
    .filter((concept) => bank.templatesFor(concept.id).length > 0)
    .map((concept) => ({ id: concept.id, order: conceptLevelOrder(registry, concept.id) }))
    .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));

  if (teachable.length === 0) return [];

  /*
   * The top `count` rungs the student has reached, rather than the top `count`
   * concepts: taking the last three off a sorted list hands back three concepts
   * from the same unit, and a trial that asks one question about arithmetic
   * three times is not a trial. Walking back from the hardest and taking one
   * per level spreads it over the ground they actually covered.
   */
  const perLevel = new Map<number, string>();
  for (const entry of teachable) {
    if (entry.order > level) continue;
    if (!perLevel.has(entry.order)) perLevel.set(entry.order, entry.id);
  }
  const chosen = [...perLevel.entries()]
    .sort((a, b) => a[0] - b[0])
    .slice(-count)
    .map(([, id]) => id);

  /*
   * A student standing on the very floor of the curriculum has fewer rungs
   * beneath them than the trial has stages, so it tops up with the easiest
   * mathematics there is — reaching *upward* only when there is nothing else,
   * and only as far as it must. A beginner who walked the north road gets three
   * stages like everybody else; handing them a one-stage trial, or none, would
   * be a locked door dressed as an empty island.
   */
  for (const entry of teachable) {
    if (chosen.length >= count) break;
    if (!chosen.includes(entry.id)) chosen.push(entry.id);
  }
  return chosen.slice(0, count);
}

/**
 * The concepts a *breadth* trial asks about — one per strand, easiest first.
 *
 * The south road's answer to the north road, and the difference is the point of
 * having two. The northern trial goes *deep*: one concept per level, climbing
 * to the hardest rung the student has reached, asking "how far up have you
 * got". This goes *wide*: one concept from each of as many different strands as
 * it can find at or below their level, asking "how much of the map do you carry
 * with you". A student who has driven one strand hard and left the others alone
 * finds the north road easier than the south, which is a true thing to be told
 * and a thing no single number about them could say.
 *
 * Same rules as the depth version otherwise: only concepts the bank can
 * generate for, sorted deterministically, and topped up from the easiest
 * mathematics there is rather than handing a beginner a short trial.
 */
export function breadthConceptIds(
  registry: CurriculumRegistry,
  bank: ExerciseBank,
  level: number,
  count: number = SOUTH_TRIAL_STAGES,
): string[] {
  const teachable = [...registry.graph.concepts.values()]
    .filter((concept) => concept.status === 'available')
    .filter((concept) => bank.templatesFor(concept.id).length > 0)
    .map((concept) => ({
      id: concept.id,
      strand: concept.strand,
      order: conceptLevelOrder(registry, concept.id),
    }))
    .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));

  if (teachable.length === 0) return [];

  /*
   * The *hardest* concept the student has reached in each strand, so a breadth
   * trial is still pitched at them rather than at the bottom of every strand
   * they have ever touched. Strands are then taken in curriculum order, which
   * keeps the walk through them recognisable rather than alphabetical.
   */
  const perStrand = new Map<string, { id: string; order: number }>();
  for (const entry of teachable) {
    if (entry.order > level) continue;
    perStrand.set(entry.strand, { id: entry.id, order: entry.order });
  }

  const chosen = [...perStrand.values()]
    .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id))
    .slice(-count)
    .map((entry) => entry.id);

  // Fewer strands than stages — an early student, who has met one or two. Top
  // up from the easiest mathematics there is, as the depth trial does.
  for (const entry of teachable) {
    if (chosen.length >= count) break;
    if (!chosen.includes(entry.id)) chosen.push(entry.id);
  }
  return chosen.slice(0, count);
}

/**
 * The band a stage opens at, from where the concept sits under the student.
 *
 * The last stage is the hardest thing they have reached, so it opens a notch
 * above the first. Bounded to 2–4: never 1, because a trial at the end of the
 * longest road on the map should not open at the easiest question in the bank,
 * and never 5, because an adaptive stage that opens at the ceiling can only go
 * down and the climb is the point.
 */
export function trialBand(stageIndex: number, stageCount: number): Difficulty {
  if (stageCount <= 1) return 3;
  const step = stageIndex / (stageCount - 1);
  return (step < 0.5 ? 2 : step < 1 ? 3 : 4) as Difficulty;
}

/**
 * The trial, built for one student.
 *
 * `level` is the caller's `workingLevel` — passed in rather than computed here
 * so this package keeps knowing nothing about how a level is decided, which is
 * the same arrangement `siteEncounter` has with mastery.
 *
 * Returns `undefined` when the curriculum can offer nothing at all, which the
 * page treats as "the trial is not running today" rather than as an error. That
 * cannot happen with the shipped content and `validateContent` would catch it,
 * but a fight that throws on an empty stage list is worse than one that says so.
 */
export function northTrial(
  registry: CurriculumRegistry,
  bank: ExerciseBank,
  level: number,
): EncounterDef | undefined {
  const conceptIds = trialConceptIds(registry, bank, level);
  if (conceptIds.length === 0) return undefined;

  return {
    id: trialBattleId(NORTH_TRIAL_ID),
    stages: conceptIds.map((conceptId, index) => ({
      name: registry.concept(conceptId)?.title ?? conceptId,
      conceptIds: [conceptId],
      difficulty: trialBand(index, conceptIds.length),
      questionCount: TRIAL_QUESTIONS,
      /*
       * Adaptive, like a warded site and unlike a boss. A boss stage is fixed
       * because a ceremony is the same mountain for everyone who walks up to
       * it; this is the opposite kind of thing — a trial pitched at whoever
       * turned up — so it must be able to ease as well as climb.
       */
      banding: 'adaptive' as const,
    })),
  };
}

/**
 * The southern trial: four stages, one per strand, at the end of the long road.
 *
 * Built the same way and under the same rules as `northTrial` — in front of the
 * isle's lock, from a level the caller passes in, adaptive within the fight.
 * Only the *selection* differs, and that is the whole of what makes walking
 * both roads worth doing.
 */
export function southTrial(
  registry: CurriculumRegistry,
  bank: ExerciseBank,
  level: number,
): EncounterDef | undefined {
  const conceptIds = breadthConceptIds(registry, bank, level);
  if (conceptIds.length === 0) return undefined;

  return {
    id: trialBattleId(SOUTH_TRIAL_ID),
    stages: conceptIds.map((conceptId, index) => ({
      name: registry.concept(conceptId)?.title ?? conceptId,
      conceptIds: [conceptId],
      difficulty: trialBand(index, conceptIds.length),
      questionCount: TRIAL_QUESTIONS,
      banding: 'adaptive' as const,
    })),
  };
}

/** Either trial, by id. What a page has after reading its route parameter. */
export function trialFor(
  trialId: string,
  registry: CurriculumRegistry,
  bank: ExerciseBank,
  level: number,
): EncounterDef | undefined {
  if (trialId === NORTH_TRIAL_ID) return northTrial(registry, bank, level);
  if (trialId === SOUTH_TRIAL_ID) return southTrial(registry, bank, level);
  return undefined;
}

/** Whether this student has already been paid for clearing the trial. */
export function trialCleared(trialId: string, profile: LearnerProfile): boolean {
  return profile.rpg?.claimedQuestIds.includes(trialClaimId(trialId)) ?? false;
}

/**
 * Pays for a cleared trial, once.
 *
 * The same idempotent claim a quest, a boss and a site all settle through, so a
 * trial fought again for the pleasure of it is paid nothing — which is what
 * keeps a reward from being a thing to farm. Anything other than a win returns
 * the profile untouched.
 *
 * Note what is *not* here: no mastery, no concept state, no flag saying the
 * trial was passed. Every answer in the fight already went through
 * `recordAttempt` on the ordinary path, exactly as a practice answer does, so
 * the mathematics has been recorded by the time this runs. A boss cannot be a
 * place where mathematics happens and is not recorded, and neither can this.
 */
export function settleTrialVictory(
  profile: LearnerProfile,
  trialId: string,
  outcome: string,
): LearnerProfile {
  if (outcome !== 'won') return profile;
  return claimQuestReward(profile, {
    questId: trialClaimId(trialId),
    xp: TRIAL_XP,
    gold: TRIAL_GOLD,
  });
}
